import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, where, orderBy, getDocs, doc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import CourseCard from '../teacher/CourseCard';
import { useProgramGroupedCourses } from '../../hooks/useProgramGroupedCourses';

interface Course {
    id: string;
    name: string;
    nameEn?: string;
    avatar?: string;
    scheduleInfo: string;
    status: 'active' | 'pending' | 'completed' | 'cancelled';
    location?: string;
    description?: string;
    hasTodayClass?: boolean;
}

const TeacherCourses: React.FC = () => {
    const { t } = useTranslation();
    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    // 使用 useMemo 缓存日期对象，避免每次渲染都创建新的对象
    const { today, tomorrow, todayTimestamp, tomorrowTimestamp } = useMemo(() => {
        // 获取今天的日期（0时0分0秒）
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 获取明天的日期（0时0分0秒）
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        // 转换为Timestamp
        const todayTimestamp = Timestamp.fromDate(today);
        const tomorrowTimestamp = Timestamp.fromDate(tomorrow);
        
        return { today, tomorrow, todayTimestamp, tomorrowTimestamp };
    }, []);

    useEffect(() => {
        const fetchTeacherCourses = async () => {
            if (!auth.currentUser) return;
            
            try {
                setLoading(true);
                setError(null);
                
                // 创建教师引用
                const teacherRef = doc(db, 'users', auth.currentUser.uid);
                
                // 查询教师负责的所有课程 - 使用 array-contains 查询，因为 teacher 是一个数组
                // 不再用 course.status 过滤（课程跨项目共享，状态无意义），改为在展示层按「active 项目」筛选
                const coursesQuery = query(
                    collection(db, 'courses'),
                    where('teacher', 'array-contains', teacherRef),
                    orderBy('createdAt', 'desc')
                );
                
                const querySnapshot = await getDocs(coursesQuery);
                if (querySnapshot.empty) {
                    setCourses([]);
                    setLoading(false);
                    return;
                }
                
                let coursesData: Course[] = querySnapshot.docs.map(docSnapshot => {
                    const data = docSnapshot.data();
                    
                    return {
                        id: docSnapshot.id,
                        name: data.name || '',
                        nameEn: data.nameEn || '',
                        avatar: data.avatar || null,
                        scheduleInfo: data.scheduleInfo || '',
                        status: data.status || 'pending',
                        location: data.location || '',
                        description: data.description || '',
                        hasTodayClass: false
                    };
                });
                
                // 如果有相关数据，可以设置hasTodayClass
                // 注意：这里需要根据新的scheduleInfo字段逻辑来判断是否有今日课程
                // 如果scheduleInfo没有明确指示今日课程信息，可能需要其他方法判断
                
                // 按今日课程排序（如果有相关标记）
                coursesData.sort((a, b) => {
                    // 首先按照今日课程排序
                    if (a.hasTodayClass && !b.hasTodayClass) return -1;
                    if (!a.hasTodayClass && b.hasTodayClass) return 1;
                    return 0;
                });
                
                setCourses(coursesData);
            } catch (err) {
                console.error('获取教师课程失败:', err);
                setError(t('profile.coursesFetchError'));
            } finally {
                setLoading(false);
            }
        };
        
        fetchTeacherCourses();
    }, [t, today, tomorrow]); // 添加依赖项

    // 将课程按项目分组，只看「进行中(active)」项目下的课程
    const groups = useProgramGroupedCourses(courses, 'active');
    
    
    if (loading) {
        return (
            <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="text-center py-8">
                    <p className="text-red-300">{error}</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">{t('profile.activeCourses')}</h2>
                <Link 
                    to="/teacher/courses" 
                    className="flex items-center text-blue-300 hover:text-white transition-colors"
                >
                    <span className="mr-1">{t('profile.viewAllCourses')}</span>
                    <ArrowRightIcon className="h-4 w-4" />
                </Link>
            </div>
            
            {courses.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-blue-100">{t('profile.noActiveCourses')}</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {groups.map(group => (
                        <div key={group.programId || 'ungrouped'}>
                            <h3 className="text-base font-semibold text-blue-100 mb-3 flex items-center">
                                {group.title}
                                {group.subtitle && <span className="ml-2 text-sm font-normal text-blue-300">({group.subtitle})</span>}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {group.courses.map((course, index) => (
                                    <CourseCard 
                                        key={`${group.programId}_${course.id}`}
                                        course={course}
                                        index={index}
                                        programId={group.programId || undefined}
                                        programName={group.title}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TeacherCourses; 